import { BeLinkCOS } from '@be-link/cos';
import {
	Button,
	Input,
	message,
	Modal,
	Radio,
	Space,
	Spin,
	Tabs,
	Tag,
	Typography,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import HotUpdateConfigPanel from './components/HotUpdateConfigPanel';
import JsonTreeEditor, { type JsonValue } from './components/JsonTreeEditor';
import ShowHotUpdateBtnConfigPanel from './components/ShowHotUpdateBtnConfigPanel';
import { type CosUploader, uploadTextToCosPath } from './utils/cosUpload';
import { formatYmdHmsZh } from './utils/formatDateTime';
import { formatJsonText } from './utils/formatJsonText';
import { buildJsonDiff } from './utils/jsonDiff';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;
const JSON_INDENT = 4;

type AppConfigEnv = 'test' | 'production';

const APP_CONFIG_ENV_OPTIONS: {
	label: string;
	value: AppConfigEnv;
	url: string;
	tagColor: string;
}[] = [
	{
		label: '测试环境',
		value: 'test',
		url: 'https://cdn.8848top.com/pandora-miniprogram/app-update/dev/config.json',
		tagColor: 'gold',
	},
	{
		label: '生产环境',
		value: 'production',
		url: 'https://cdn.8848top.com/pandora-miniprogram/app-update/prod/config.json',
		tagColor: 'red',
	},
];

function formatConfigContent(content: string) {
	return JSON.stringify(JSON.parse(content), null, JSON_INDENT);
}

function getErrorMessage(error: unknown) {
	if (error instanceof Error) {
		return error.message;
	}

	return '未知错误';
}

function formatDiffPath(pathKey: string) {
	const relativePath = pathKey.replace(/^config\.json/, '');

	if (!relativePath) {
		return '根节点';
	}

	return relativePath.replace(/^\./, '');
}

function getDiffKindColor(kind: string) {
	if (kind === '新增') {
		return 'green';
	}

	if (kind === '删除') {
		return 'red';
	}

	return 'blue';
}

export default function DevConfig() {
	const [activeTab, setActiveTab] = useState('app-config');
	const [hotUpdateRefreshKey, setHotUpdateRefreshKey] = useState(0);
	const [showHotUpdateBtnRefreshKey, setShowHotUpdateBtnRefreshKey] =
		useState(0);
	const [env, setEnv] = useState<AppConfigEnv>('test');
	const [jsonValue, setJsonValue] = useState<JsonValue | null>(null);
	const [lastSavedContent, setLastSavedContent] = useState('');
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [lastModified, setLastModified] = useState('');
	const [activeJsonPathKey, setActiveJsonPathKey] = useState('config.json');
	const [textAreaElement, setTextAreaElement] =
		useState<HTMLTextAreaElement | null>(null);

	const cosUploader = useMemo(
		() =>
			new BeLinkCOS({
				mode: 'production',
			}) as unknown as CosUploader,
		[],
	);

	const currentConfig = useMemo(() => {
		return (
			APP_CONFIG_ENV_OPTIONS.find((item) => item.value === env) ||
			APP_CONFIG_ENV_OPTIONS[0]
		);
	}, [env]);

	const loadConfig = useCallback(async () => {
		setLoading(true);

		try {
			const response = await fetch(`${currentConfig.url}?t=${Date.now()}`, {
				cache: 'no-store',
			});

			if (!response.ok) {
				throw new Error(`读取失败，状态码：${response.status}`);
			}

			const text = await response.text();
			const formattedContent = formatConfigContent(text);
			const parsedValue = JSON.parse(formattedContent) as JsonValue;

			setJsonValue(parsedValue);
			setLastSavedContent(formattedContent);
			setLastModified(
				formatYmdHmsZh(response.headers.get('Last-Modified') || ''),
			);
		} catch (error) {
			message.error(`读取配置失败：${getErrorMessage(error)}`);
		} finally {
			setLoading(false);
		}
	}, [currentConfig.url]);

	useEffect(() => {
		void loadConfig();
	}, [loadConfig]);

	const jsonContent = useMemo(() => {
		if (jsonValue === null) {
			return '';
		}

		return JSON.stringify(jsonValue, null, JSON_INDENT);
	}, [jsonValue]);

	const jsonTextMap = useMemo(() => {
		if (jsonValue === null) {
			return null;
		}

		return formatJsonText(jsonValue);
	}, [jsonValue]);

	const hasChanges = jsonContent !== lastSavedContent;

	const handleNodeActivate = useCallback((pathKey: string) => {
		setActiveJsonPathKey(pathKey);
	}, []);

	useEffect(() => {
		if (!jsonTextMap) {
			return;
		}

		const lineIndex = jsonTextMap.pathLineMap[activeJsonPathKey] ?? 0;
		if (!textAreaElement) {
			return;
		}

		const lineHeight =
			Number.parseFloat(getComputedStyle(textAreaElement).lineHeight) || 24;
		textAreaElement.scrollTop = Math.max(
			0,
			lineIndex * lineHeight - lineHeight * 2,
		);
	}, [activeJsonPathKey, jsonTextMap, textAreaElement]);

	const handleSave = useCallback(async () => {
		if (jsonValue === null) {
			message.error('当前没有可保存的 JSON 内容');
			return;
		}

		let formattedContent = '';

		try {
			formattedContent = formatConfigContent(jsonContent);
		} catch (error) {
			message.error(`JSON 格式不合法：${getErrorMessage(error)}`);
			return;
		}

		let savedJsonValue: JsonValue | null = null;

		if (lastSavedContent) {
			try {
				savedJsonValue = JSON.parse(lastSavedContent) as JsonValue;
			} catch {
				savedJsonValue = null;
			}
		}

		const nextJsonValue = JSON.parse(formattedContent) as JsonValue;
		const diffSummary = savedJsonValue
			? buildJsonDiff(savedJsonValue, nextJsonValue)
			: null;

		Modal.confirm({
			title: '保存前确认',
			width: 960,
			okText: '确认保存',
			cancelText: '取消',
			content: (
				<Space direction="vertical" size={12} style={{ width: '100%' }}>
					<Text type="secondary">请先确认本次修改内容。</Text>
					{diffSummary ? (
						<Space wrap>
							<Text>新增 {diffSummary.added}</Text>
							<Text>删除 {diffSummary.removed}</Text>
							<Text>修改 {diffSummary.changed}</Text>
						</Space>
					) : null}
					<div style={{ maxHeight: 420, overflow: 'auto' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse' }}>
							<thead>
								<tr>
									<th style={{ textAlign: 'left' }}>类型</th>
									<th style={{ textAlign: 'left' }}>路径</th>
									<th style={{ textAlign: 'left' }}>修改前</th>
									<th style={{ textAlign: 'left' }}>修改后</th>
								</tr>
							</thead>
							<tbody>
								{diffSummary?.items.length ? (
									diffSummary.items.map((item) => (
										<tr key={`${item.kind}-${item.pathKey}`}>
											<td>
												<Tag color={getDiffKindColor(item.kind)}>
													{item.kind}
												</Tag>
											</td>
											<td>{formatDiffPath(item.pathKey)}</td>
											<td>
												<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
													{item.beforeText}
												</pre>
											</td>
											<td>
												<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
													{item.afterText}
												</pre>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan={4}>没有检测到差异</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</Space>
			),
			onOk: async () => {
				setSaving(true);

				try {
					await uploadTextToCosPath(cosUploader, {
						path: new URL(currentConfig.url).pathname,
						body: formattedContent,
						contentType: 'application/json; charset=utf-8',
					});

					setLastSavedContent(formattedContent);
					setLastModified(formatYmdHmsZh(new Date()));
					message.success(
						`${currentConfig.label}配置保存成功，已覆盖远程 config.json`,
					);
				} catch (error) {
					message.error(`保存失败：${getErrorMessage(error)}`);
				} finally {
					setSaving(false);
				}
			},
		});
	}, [cosUploader, currentConfig, jsonContent, jsonValue, lastSavedContent]);

	const handleTabChange = useCallback(
		(key: string) => {
			setActiveTab(key);
			if (key === 'app-config') {
				void loadConfig();
				return;
			}
			if (key === 'hot-update-config') {
				setHotUpdateRefreshKey((value) => value + 1);
				return;
			}
			if (key === 'show-hot-update-btn-config') {
				setShowHotUpdateBtnRefreshKey((value) => value + 1);
			}
		},
		[loadConfig],
	);

	const tabItems = [
		{
			key: 'app-config',
			label: 'APP配置',
			children: (
				<Spin spinning={loading} tip="读取配置中...">
					<Space direction="vertical" size="middle" style={{ width: '100%' }}>
						<Space direction="vertical" size={4} style={{ width: '100%' }}>
							<Text strong>环境选择</Text>
							<Radio.Group
								value={env}
								onChange={(e) => setEnv(e.target.value)}
								options={APP_CONFIG_ENV_OPTIONS.map(({ label, value }) => ({
									label,
									value,
								}))}
								optionType="button"
								buttonStyle="solid"
								disabled={loading || saving}
							/>
						</Space>

						<Space direction="vertical" size={4} style={{ width: '100%' }}>
							<Text strong>配置地址</Text>
							<Paragraph
								copyable={{ text: currentConfig.url }}
								style={{ marginBottom: 0 }}
							>
								{currentConfig.url}
							</Paragraph>
						</Space>

						<Space direction="vertical" size={4} style={{ width: '100%' }}>
							<Text strong>最近读取/保存时间</Text>
							<Text type="secondary">{lastModified || '-'}</Text>
						</Space>

						<div
							style={{
								display: 'flex',
								gap: 16,
								alignItems: 'stretch',
								flexWrap: 'wrap',
								minHeight: 500,
							}}
						>
							<div
								style={{
									flex: '1 1 520px',
									minWidth: 360,
									height: 500,
									minHeight: 0,
								}}
							>
								<JsonTreeEditor
									value={jsonValue}
									onChange={setJsonValue}
									height={500}
									onNodeActivate={handleNodeActivate}
								/>
							</div>

							<div
								style={{
									flex: '1 1 520px',
									minWidth: 360,
									height: 500,
									minHeight: 0,
									display: 'flex',
									flexDirection: 'column',
									gap: 8,
								}}
							>
								<Text strong>原文内容</Text>
								<TextArea
									ref={(instance) => {
										setTextAreaElement(
											instance?.resizableTextArea?.textArea ?? null,
										);
									}}
									value={jsonContent}
									readOnly
									autoSize={false}
									style={{
										flex: 1,
										height: 500,
										minHeight: 0,
										resize: 'none',
										fontFamily: 'Menlo, Monaco, Consolas, monospace',
									}}
								/>
							</div>
						</div>

						<Space wrap>
							<Button
								onClick={() => void loadConfig()}
								loading={loading || saving}
							>
								重新读取
							</Button>
							<Button
								type="primary"
								onClick={() => void handleSave()}
								loading={saving}
								disabled={!jsonContent || loading || !hasChanges}
							>
								保存
							</Button>
						</Space>
					</Space>
				</Spin>
			),
		},
		{
			key: 'hot-update-config',
			label: 'APP热更新发布/检查配置',
			children: <HotUpdateConfigPanel refreshKey={hotUpdateRefreshKey} />,
		},
		{
			key: 'show-hot-update-btn-config',
			label: '热更新检查按钮显示配置',
			children: (
				<ShowHotUpdateBtnConfigPanel refreshKey={showHotUpdateBtnRefreshKey} />
			),
		},
	];

	return (
		<Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />
	);
}
